import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Email, PictureAsPdf } from '@mui/icons-material';
import { toast } from 'react-toastify';
import type { StockPlan } from '../../../../models/StockPickTypes';
import type { PlanExecutionData } from '../../../../models/PlanExecutionTypes';
import { getPlanExecution } from '../../../../services/request/planExecutionApi';
import {
  sendStockPlanReportEmail,
  type StockPlanReportViewMode,
} from '../../../../services/request/stockPlanReportApi';
import { exportStockPlanPdf, type StockPlanPdfOptions } from '../../../../utils/stockPickPdf';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface PlanReportDialogProps {
  open: boolean;
  onClose: () => void;
  plan: StockPlan;
}

const PlanReportDialog: React.FC<PlanReportDialogProps> = ({ open, onClose, plan }) => {
  const [viewMode, setViewMode] = useState<StockPlanReportViewMode>('ADMIN');
  const [partnerId, setPartnerId] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [recipientEmails, setRecipientEmails] = useState<string[]>([]);
  const [subject, setSubject] = useState('');
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [exporting, setExporting] = useState(false);

  const partnerOptions = useMemo(() => {
    const map = new Map<string, string>();
    (plan.capitalAllocations ?? []).forEach((a) => {
      if (a.capitalPartnerId) {
        map.set(a.capitalPartnerId, a.partnerName ?? `Partner ${a.capitalPartnerId}`);
      }
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [plan.capitalAllocations]);

  const addEmail = useCallback(() => {
    const trimmed = emailInput.trim();
    if (!trimmed) return;
    if (!EMAIL_RE.test(trimmed)) {
      toast.error('Enter a valid email address.');
      return;
    }
    if (recipientEmails.some((e) => e.toLowerCase() === trimmed.toLowerCase())) {
      toast.info('Email already added.');
      return;
    }
    setRecipientEmails((prev) => [...prev, trimmed]);
    setEmailInput('');
  }, [emailInput, recipientEmails]);

  const removeEmail = (email: string) => {
    setRecipientEmails((prev) => prev.filter((e) => e !== email));
  };

  const pdfOptions = useCallback((): StockPlanPdfOptions => ({
    view: viewMode,
    partnerId: viewMode === 'PARTNER' && partnerId ? partnerId : undefined,
  }), [viewMode, partnerId]);

  const loadExecution = async (): Promise<PlanExecutionData | undefined> => {
    if (!plan.id) return undefined;
    try {
      return await getPlanExecution(plan.id);
    } catch {
      toast.warn('Could not load execution data; PDF/email may omit execution section.');
      return undefined;
    }
  };

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      const execution = await loadExecution();
      exportStockPlanPdf(plan, {
        ...pdfOptions(),
        execution,
      });
      toast.success('PDF downloaded.');
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'PDF export failed.');
    } finally {
      setExporting(false);
    }
  };

  const handleSendEmail = async () => {
    if (!plan.id) return;
    if (recipientEmails.length === 0) {
      toast.error('Add at least one recipient email.');
      return;
    }
    setSending(true);
    try {
      const result = await sendStockPlanReportEmail(plan.id, {
        recipientEmails,
        viewMode,
        partnerId: viewMode === 'PARTNER' && partnerId ? partnerId : undefined,
        subject: subject.trim() || undefined,
        note: note.trim() || undefined,
      });
      toast.success(`Report sent to ${result.emailsSent} recipient(s).`);
      onClose();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to send report email.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Plan report — email &amp; PDF</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {plan.planCode ?? plan.id} · Choose admin view (full detail including profit) or partner view (no P&amp;L).
          </Typography>

          <FormControl component="fieldset">
            <FormLabel component="legend">Report content</FormLabel>
            <RadioGroup
              row
              value={viewMode}
              onChange={(_, v) => setViewMode(v as StockPlanReportViewMode)}
            >
              <FormControlLabel value="ADMIN" control={<Radio />} label="Admin view" />
              <FormControlLabel value="PARTNER" control={<Radio />} label="Partner view" />
            </RadioGroup>
          </FormControl>

          {viewMode === 'PARTNER' && partnerOptions.length > 0 && (
            <FormControl fullWidth size="small">
              <InputLabel id="report-partner-label">Scope to partner (optional)</InputLabel>
              <Select
                labelId="report-partner-label"
                label="Scope to partner (optional)"
                value={partnerId}
                onChange={(e) => setPartnerId(e.target.value)}
              >
                <MenuItem value="">All partners on plan</MenuItem>
                {partnerOptions.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {viewMode === 'PARTNER' && (
            <Alert severity="info" variant="outlined">
              Partner view excludes profit, tax, and P&amp;L — same as the partner portal.
            </Alert>
          )}

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Recipient emails
            </Typography>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <TextField
                size="small"
                fullWidth
                label="Add email"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addEmail();
                  }
                }}
                placeholder="name@example.com"
              />
              <Button variant="outlined" onClick={addEmail} sx={{ flexShrink: 0, mt: 0.25 }}>
                Add
              </Button>
            </Stack>
            {recipientEmails.length > 0 && (
              <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1 }}>
                {recipientEmails.map((email) => (
                  <Chip key={email} label={email} onDelete={() => removeEmail(email)} size="small" />
                ))}
              </Stack>
            )}
          </Box>

          <TextField
            size="small"
            fullWidth
            label="Email subject (optional)"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Defaults to plan code + view type"
          />
          <TextField
            size="small"
            fullWidth
            multiline
            minRows={2}
            label="Note prepended to email (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="outlined"
          startIcon={<PictureAsPdf />}
          disabled={exporting}
          onClick={() => void handleExportPdf()}
        >
          Export PDF
        </Button>
        <Button
          variant="contained"
          startIcon={<Email />}
          disabled={sending || recipientEmails.length === 0}
          onClick={() => void handleSendEmail()}
        >
          Send email
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlanReportDialog;
